import {withPayloadType} from '@lib/utils';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {AppThunkApiConfig, AppThunk} from '../../createStore';
import {
  saveLastSelectedWorkspaceReservationLocation,
  updateWorkspaceReservation,
  registerWorkspaceReservation,
  createGroupWorkspaceReservation,
  removeWorkspaceReservation,
} from '../../data/workspaceReservation';
import {getBuildingById, getBuildingIds} from '../../data/buildings';
import {getFloorById} from '../../data/floors';
import {getParkingReservationByDate, cancelParkingReservation} from '../../data/parkingReservation';
import {GroupReservationInviteeSearchResult, Workday, WorkspaceReservation} from '../../data/entities';
import {updateCheckInStatus} from '../../data/checkIn';
import {CheckInEventType, CheckInSourceType} from '@lib/infrastructure';
import {handleError} from '../../infrastructure';
import {loadMeetings} from '../../data/meetings';
import {cancelWorkday, getWorkdayByDate, loadWorkdays, replaceWorkday} from '../../data/workday';
import {getParkingLotsByBuildingId} from '../../data/parkingLot';
import {SaveGroupWorkspaceReservationParams, SaveWorkspaceReservationParams} from './types';

export const cancelParkingReservationAndLoadWorkdayGeneric =
  (buildingId: string, selectedDate: string, workspaceReservationId?: string): AppThunk =>
  async (dispatch, getState) => {
    const parkingReservation = getParkingReservationByDate(getState(), selectedDate);
    const workdayPrevious = getWorkdayByDate(getState(), selectedDate);

    if (parkingReservation && workdayPrevious?.status === 'OfficeDay' && workdayPrevious?.nodeId !== buildingId) {
      const parkingLots = getParkingLotsByBuildingId(getState(), buildingId);
      const foundParkingLot = parkingLots.find((parkingLot) => parkingLot.id === parkingReservation.parkingLotId);
      !foundParkingLot && dispatch(cancelParkingReservation(parkingReservation.id));
    }

    dispatch(loadWorkdays({from: selectedDate, to: selectedDate})).unwrap();

    await dispatch(
      updateCheckInStatus({
        checkInStatus: {
          eventType: CheckInEventType.AtTheOffice,
          buildingId: buildingId,
          sourceType: CheckInSourceType.Manual,
        },
        workspaceReservation: {startDate: selectedDate, id: workspaceReservationId},
      }),
    );
  };

export const saveWorkspaceReservation = createAsyncThunk<void, SaveWorkspaceReservationParams, AppThunkApiConfig>(
  'ui/workspaceReservation/saveWorkspaceReservation',
  async (
    {location, nodeIds, selectedDate, workspaceReservationId, invitees, buildingName},
    {dispatch, getState, rejectWithValue},
  ) => {
    const {buildingId, floorId, areaId, deskId} = location;
    const actualState = getState();
    const workdayPrevious = getWorkdayByDate(actualState, selectedDate);
    const parkingReservation = getParkingReservationByDate(actualState, selectedDate);
    try {
      await dispatch(saveLastSelectedWorkspaceReservationLocation({buildingId, floorId, areaId, deskId})).unwrap();

      if (workspaceReservationId) {
        await dispatch(updateWorkspaceReservation({nodeIds, selectedDate, workspaceReservationId, invitees})).unwrap();

        if (parkingReservation && workdayPrevious?.nodeId !== buildingId) {
          const parkingLots = getParkingLotsByBuildingId(getState(), buildingId);
          const foundParkingLot = parkingLots.find((parkingLot) => parkingLot.id === parkingReservation.parkingLotId);
          !foundParkingLot && dispatch(cancelParkingReservation(parkingReservation.id));
        }

        //if building is changed and isWorkdayEnabled,change workday
        workdayPrevious &&
          workdayPrevious.nodeId !== buildingId &&
          dispatch(
            replaceWorkday({
              id: workdayPrevious.id,
              nodeId: buildingId,
              nodeName: buildingName,
              startDate: workdayPrevious.startDate,
              endDate: workdayPrevious.endDate,
              status: 'OfficeDay',
            } as Workday),
          );
      } else {
        await dispatch(registerWorkspaceReservation({nodeIds, selectedDate})).unwrap();
        dispatch(cancelParkingReservationAndLoadWorkdayGeneric(buildingId, selectedDate, workspaceReservationId));
      }
      // TODO: Revisit this, it's not nice to do it from here.
      // We don't want to wait loading meetings to complete
      dispatch(loadMeetings({from: selectedDate, to: selectedDate}));
    } catch (error) {
      if (invitees?.length) {
        return rejectWithValue(error);
      }
      handleError(error, dispatch);
      return rejectWithValue('Failed saving the workspace reservation');
    }
  },
);

export const saveGroupWorkspaceReservation = createAsyncThunk<
  void,
  SaveGroupWorkspaceReservationParams,
  AppThunkApiConfig
>(
  'ui/workspaceReservation/saveGroupWorkspaceReservation',
  async (
    {invitees, nodeIds, selectedDate, workspaceReservationId, location, buildingName},
    {dispatch, rejectWithValue},
  ) => {
    const {buildingId, floorId, areaId, deskId} = location;
    try {
      if (workspaceReservationId) {
        await dispatch(
          saveWorkspaceReservation({workspaceReservationId, nodeIds, location, selectedDate, invitees, buildingName}),
        ).unwrap();
        return;
      }
      await dispatch(saveLastSelectedWorkspaceReservationLocation({buildingId, floorId, areaId, deskId})).unwrap();
      await dispatch(
        createGroupWorkspaceReservation({
          nodeIds,
          invitees,
          selectedDate,
        }),
      ).unwrap();
      dispatch(cancelParkingReservationAndLoadWorkdayGeneric(buildingId, selectedDate, workspaceReservationId));
    } catch (error) {
      throw rejectWithValue(error);
    }
  },
);

export const selectLocationBasedOnBuildingId =
  (buildingId: string): AppThunk =>
  (dispatch, getState) => {
    const actualState = getState();
    const building = getBuildingById(actualState, buildingId);
    const buildings = getBuildingIds(actualState);
    const actualBuildingId = buildings.includes(buildingId)
      ? buildingId
      : buildings.length === 1
      ? (buildings[0] as string)
      : '';
    const floorId = building && building.floors.length === 1 ? building.floors[0] : '';
    const floor = getFloorById(actualState, floorId);
    const areaId = floor && floor.areas.length === 1 ? floor.areas[0] : '';
    const preselectedFloorId = floor && floor.areas.length <= 1 ? floorId : '';

    dispatch(selectLocation({buildingId: actualBuildingId, floorId: preselectedFloorId, areaId}));
  };

export const deleteWorkspaceReservation =
  (workspaceReservation: WorkspaceReservation): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    await dispatch(removeWorkspaceReservation(workspaceReservation)).unwrap();
    const startDate = workspaceReservation.startDate;

    const workday = getWorkdayByDate(state, startDate);
    const building = getBuildingById(state, workspaceReservation.buildingId);
    const shouldCancelWorkday = workday && building && !building.allowWorkdayWithoutWorkspace;
    shouldCancelWorkday && dispatch(cancelWorkday(workday.id));

    const parkingReservation = getParkingReservationByDate(state, startDate);
    const shouldCancelParkingReservation = parkingReservation && shouldCancelWorkday;
    shouldCancelParkingReservation && dispatch(cancelParkingReservation(parkingReservation.id));
  };

export const selectFloor = createAction('ui/workspaceReservation/floorSelected', withPayloadType<string>());

export const selectArea = createAction('ui/workspaceReservation/areaSelected', withPayloadType<string>());

export const selectDesks = createAction('ui/workspaceReservation/desksSelected', withPayloadType<string[]>());

export const selectLocation = createAction(
  'ui/workspaceReservation/selectLocation',
  withPayloadType<{buildingId: string; floorId: string; areaId: string}>(),
);

export const resetWorkspaceReservation = createAction(
  'ui/workspaceReservation/resetWorkspaceReservation',
  withPayloadType<void>(),
);

export const inviteesSelected = createAction(
  'ui/workspaceReservation/inviteesSelected',
  withPayloadType<GroupReservationInviteeSearchResult[]>(),
);
